<template>
  <div class="container-fluid mt-4" :class="themeClass">
    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="bi bi-person-badge me-2"></i>
          Listado de Empleados
        </h5>
        <router-link to="/personas/empleados/nuevo" class="btn btn-primary">
          <i class="bi bi-plus-circle me-1"></i>
          Nuevo Empleado
        </router-link>
      </div>
      <div class="card-body">
        <div v-if="error" class="alert alert-danger" role="alert">
          <i class="bi bi-exclamation-triangle me-2"></i>
          {{ error }}
          <button type="button" class="btn btn-sm btn-outline-danger ms-2" @click="loadEmpleados">
            <i class="bi bi-arrow-clockwise"></i> Reintentar
          </button>
        </div>

        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="mt-2 text-muted">Cargando empleados...</p>
        </div>

        <div v-else>
          <div class="row mb-4">
            <div class="col-md-6">
              <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-search"></i>
                </span>
                <input
                  v-model="searchTerm"
                  type="text"
                  class="form-control"
                  placeholder="Buscar por nombre, identificación o email..."
                />
              </div>
            </div>
            <div class="col-md-3">
              <select v-model="filterRole" class="form-select">
                <option value="">Todos los roles</option>
                <option value="administrador">Administrador</option>
                <option value="vendedor">Vendedor</option>
                <option value="operador">Operador</option>
              </select>
            </div>
            <div class="col-md-3">
              <select v-model="sortBy" class="form-select">
                <option value="nombre">Ordenar por nombre</option>
                <option value="identificacion">Ordenar por ID</option>
              </select>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-hover">
              <thead class="table-light">
                <tr>
                  <th>Identificación</th>
                  <th>Nombre</th>
                  <th>Rol</th>
                  <th>Teléfono</th>
                  <th>Email</th>
                  <th>Usuario</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="filteredEmpleados.length === 0">
                  <td colspan="8" class="text-center py-4 text-muted">
                    <i class="bi bi-info-circle me-2"></i>
                    No se encontraron empleados
                  </td>
                </tr>
                <tr v-else v-for="empleado in paginatedEmpleados" :key="empleado.id">
                  <td>{{ empleado.identificacion }}</td>
                  <td>{{ empleado.nombre }}</td>
                  <td>
                    <span class="badge" :class="getRolBadgeClass(empleado.rol)">
                      {{ formatRol(empleado.rol) }}
                    </span>
                  </td>
                  <td>{{ empleado.telefono }}</td>
                  <td>{{ empleado.email }}</td>
                  <td>
                    <span class="badge" :class="empleado.tiene_usuario ? 'bg-success' : 'bg-secondary'">
                      {{ empleado.tiene_usuario ? 'Sí' : 'No' }}
                    </span>
                  </td>
                  <td>
                    <div class="d-flex align-items-center justify-content-center">
                      <div class="form-check me-2">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :checked="empleado.activo"
                          @change="toggleEstadoEmpleado(empleado)"
                          :disabled="cambiandoEstado === empleado.id"
                        />
                      </div>
                      <span class="badge" :class="empleado.activo ? 'bg-success' : 'bg-danger'">
                        {{ empleado.activo ? 'Activo' : 'Inactivo' }}
                      </span>
                      <span v-if="cambiandoEstado === empleado.id" class="ms-2">
                        <div class="spinner-border spinner-border-sm" role="status">
                          <span class="visually-hidden">Cargando...</span>
                        </div>
                      </span>
                    </div>
                  </td>
                  <td>
                    <div class="btn-group" role="group">
                      <button class="btn btn-sm btn-outline-warning" @click="editarEmpleado(empleado)">
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button class="btn btn-sm btn-outline-danger" @click="mostrarConfirmacionEliminar(empleado)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <nav v-if="totalPages > 1" aria-label="Paginación de empleados" class="mt-4">
            <ul class="pagination justify-content-center">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Anterior</a>
              </li>
              <li
                v-for="page in totalPages"
                :key="page"
                class="page-item"
                :class="{ active: page === currentPage }"
              >
                <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Siguiente</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <div class="modal fade" id="confirmDeleteModal" tabindex="-1" aria-labelledby="confirmDeleteModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="confirmDeleteModalLabel">
              <i class="bi bi-exclamation-triangle text-danger me-2"></i>
              Confirmar Eliminación
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">
              ¿Está seguro de que desea eliminar al empleado <strong>{{ empleadoAEliminar?.nombre }}</strong>?
            </p>
            <p class="text-muted small mt-2">
              Esta acción elimina el registro de forma permanente.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              <i class="bi bi-x-lg me-1"></i>
              Cancelar
            </button>
            <button type="button" class="btn btn-danger" @click="confirmarEliminar" :disabled="eliminando">
              <i class="bi bi-trash me-1"></i>
              {{ eliminando ? 'Eliminando...' : 'Eliminar' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import themeManager from '@/store/theme'
import { employeeService } from '@/services/employeeService'
import { Modal } from 'bootstrap'

export default {
  name: 'ClientPersonalListado',
  computed: {
    themeClass() {
      return themeManager.themeClass
    },
    filteredEmpleados() {
      let filtered = this.empleados

      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase()
        filtered = filtered.filter(empleado =>
          (empleado.nombre || '').toLowerCase().includes(term) ||
          (empleado.identificacion || '').includes(this.searchTerm) ||
          (empleado.email || '').toLowerCase().includes(term)
        )
      }

      if (this.filterRole) {
        filtered = filtered.filter(empleado => empleado.rol === this.filterRole)
      }

      if (this.sortBy === 'nombre') {
        filtered = [...filtered].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
      } else if (this.sortBy === 'identificacion') {
        filtered = [...filtered].sort((a, b) => (a.identificacion || '').localeCompare(b.identificacion || ''))
      }

      return filtered
    },
    paginatedEmpleados() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredEmpleados.slice(start, end)
    },
    totalPages() {
      return Math.ceil(this.filteredEmpleados.length / this.itemsPerPage)
    }
  },
  data() {
    return {
      searchTerm: '',
      filterRole: '',
      sortBy: 'nombre',
      currentPage: 1,
      itemsPerPage: 10,
      empleados: [],
      loading: false,
      error: null,
      cambiandoEstado: null,
      empleadoAEliminar: null,
      eliminando: false,
      deleteModal: null
    }
  },
  mounted() {
    this.loadEmpleados()
    this.deleteModal = new Modal(document.getElementById('confirmDeleteModal'))
  },
  methods: {
    async loadEmpleados() {
      this.loading = true
      this.error = null
      try {
        const response = await employeeService.getEmployees()
        this.empleados = response.data || []
      } catch (error) {
        console.error('Error al cargar empleados:', error)
        this.error = 'No se pudieron cargar los empleados.'
      } finally {
        this.loading = false
      }
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
    },
    getRolBadgeClass(rol) {
      switch (rol) {
        case 'administrador':
          return 'bg-primary'
        case 'vendedor':
          return 'bg-success'
        case 'operador':
          return 'bg-info'
        default:
          return 'bg-secondary'
      }
    },
    formatRol(rol) {
      if (!rol) return 'Sin rol'
      return rol.charAt(0).toUpperCase() + rol.slice(1)
    },
    editarEmpleado(empleado) {
      this.$router.push(`/personas/empleados/editar/${empleado.id}`)
    },
    async toggleEstadoEmpleado(empleado) {
      this.cambiandoEstado = empleado.id
      try {
        await employeeService.updateEmployee(empleado.id, {
          is_active: !empleado.activo
        })
        empleado.activo = !empleado.activo
      } catch (error) {
        console.error('Error al cambiar estado:', error)
        this.error = 'No se pudo actualizar el estado del empleado.'
      } finally {
        this.cambiandoEstado = null
      }
    },
    mostrarConfirmacionEliminar(empleado) {
      this.empleadoAEliminar = empleado
      this.deleteModal.show()
    },
    async confirmarEliminar() {
      if (!this.empleadoAEliminar) return
      this.eliminando = true
      try {
        await employeeService.deleteEmployee(this.empleadoAEliminar.id)
        this.empleados = this.empleados.filter(empleado => empleado.id !== this.empleadoAEliminar.id)
        this.currentPage = 1
      } catch (error) {
        console.error('Error al desactivar empleado:', error)
        this.error = 'No se pudo desactivar el empleado.'
      } finally {
        this.eliminando = false
        this.deleteModal.hide()
      }
    }
  }
}
</script>


